import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  DARK_SCHEME_QUERY,
  ThemeContext,
  type ThemeContextValue,
  type ThemeMode,
  type ThemePreference,
} from './themeContext';

function readSystemTheme(): ThemeMode {
  return window.matchMedia(DARK_SCHEME_QUERY).matches ? 'dark' : 'light';
}

export interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Owns the light/dark decision for the whole app. There is no user-facing
 * choice: the mode in effect is whichever brand last resolved (`applyBrand`,
 * called by `useBrand`/`useMyBrand`), defaulting to `system` until one does.
 * `system` follows the OS `prefers-color-scheme` live. The resolved value is
 * written to `body[data-theme]`, which is what `theme/tokens.css` keys off.
 */
export function ThemeProvider({ children }: ThemeProviderProps): React.JSX.Element {
  const [brandMode, setBrandMode] = useState<ThemePreference>('system');
  const [systemTheme, setSystemTheme] = useState<ThemeMode>(readSystemTheme);

  // Track the OS preference for as long as we might need it.
  useEffect(() => {
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', onChange);
    setSystemTheme(query.matches ? 'dark' : 'light');
    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  const theme: ThemeMode = brandMode === 'system' ? systemTheme : brandMode;

  useEffect(() => {
    document.body.dataset.theme = theme;
  }, [theme]);

  const applyBrand = useCallback((mode: ThemePreference) => {
    setBrandMode(mode);
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, applyBrand }), [theme, applyBrand]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
